import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import arrowDown from '@/images/ArrowDown.svg';
import Mic from '@/images/Microphone2.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useMemo } from 'react';

const Icon = styled(MaskedIcon)`
    color: ${theme.colors.black[600]};
`;

const StyledSelect = styled(Select)`
    .MuiSelect-icon.MuiSelect-iconOutlined {
        height: 24px;
        width: 24px;
    }

    .MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root-StyledSelect.Mui-focused.MuiOutlinedInput-notchedOutline {
        border-color: red !important;
    }

    .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.black[300]};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border: solid 1px ${({ theme }) => theme.colors.secondary.main} !important;
    }
`;

const CustomMenuItem = styled(MenuItem)`
    &.Mui-selected {
        background-color: ${({ theme }) => theme.colors.black[100]} !important;
    }
`;

const Label = styled('div')<{ value: string }>`
    padding-left: ${props => (props.value === 'none' ? '30px' : '6px')};
`;

const StyledIcon = styled(Icon)`
    padding-right: 6px;
`;

const SelectedValue = styled('div')`
    display: flex;
`;

const CustomSvgIcon = (props: any) => <MaskedIcon {...props} src={arrowDown} />;

interface Props {
    ttsSelectedVoice: IVoice | null;
    value: string | undefined;
    setValue: (value: string) => void;
}

export const StylesControl = ({ ttsSelectedVoice, value, setValue }: Props) => {
    const mixpanel = useAnalytics();

    const options = useMemo(
        () => [...(ttsSelectedVoice?.styles ?? []), 'none'].map(style => ({ value: style, label: style ?? '' })),
        [ttsSelectedVoice?.styles]
    );

    if (ttsSelectedVoice?.provider === Providers.Elevenlabs) {
        return null;
    }

    if (options.length === 1) {
        return null;
    }

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const selectedStyle = event.target.value;

        if (typeof selectedStyle !== 'string') return;

        setValue(selectedStyle);

        mixpanel?.track('tts-style', { event: 'click' });
    };

    const renderSelectedValue = () => {
        const renderValue = !!value ? (
            <>
                <StyledIcon src={Mic} size="24px" />
                {value}
            </>
        ) : (
            'None'
        );

        return <SelectedValue>{renderValue}</SelectedValue>;
    };

    return (
        <FormControl>
            <InputLabel id="style-label" color="secondary">
                Voice style
            </InputLabel>
            <StyledSelect
                IconComponent={CustomSvgIcon}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    },
                    transformOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                }}
                id="select-helper"
                labelId="style-label"
                label="Voice style"
                value={value || 'none'}
                onChange={handleChange}
                renderValue={renderSelectedValue}>
                {options.map(o => (
                    <CustomMenuItem id={o.value} value={o.value} key={o.value}>
                        {o.value !== 'none' && <Icon src={Mic} size="24px" />}
                        <Label value={o.value}>{o.label}</Label>
                    </CustomMenuItem>
                ))}
            </StyledSelect>
        </FormControl>
    );
};
